// =============================================================================
// Toast Store - Sistema notifiche globale con Zustand
// =============================================================================

import { create } from 'zustand';
import { nanoid } from 'nanoid';

// =============================================================================
// Types
// =============================================================================

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  dismissible?: boolean;
}

interface ToastState {
  toasts: Toast[];
  
  // Actions
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAll: () => void;
  
  // Shorthand methods
  success: (title: string, message?: string) => string;
  error: (title: string, message?: string) => string;
  warning: (title: string, message?: string) => string;
  info: (title: string, message?: string) => string;
}

// =============================================================================
// Default durations by type
// =============================================================================

const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 3000,
};

// =============================================================================
// Store
// =============================================================================

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  addToast: (toast) => {
    const id = nanoid(8);
    const duration = toast.duration ?? DEFAULT_DURATIONS[toast.type];
    
    const newToast: Toast = {
      ...toast,
      id,
      dismissible: toast.dismissible ?? true,
    };

    set((state) => ({
      toasts: [...state.toasts, newToast],
    }));

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }

    return id;
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearAll: () => {
    set({ toasts: [] });
  },

  // Shorthand methods
  success: (title, message) => {
    return get().addToast({ type: 'success', title, message });
  },

  error: (title, message) => {
    return get().addToast({ type: 'error', title, message });
  },

  warning: (title, message) => {
    return get().addToast({ type: 'warning', title, message });
  },

  info: (title, message) => {
    return get().addToast({ type: 'info', title, message });
  },
}));

// =============================================================================
// Hook helper
// =============================================================================

export function useToast() {
  const { success, error, warning, info, removeToast, clearAll } = useToastStore();
  
  return {
    success,
    error,
    warning,
    info,
    dismiss: removeToast,
    clearAll,
  };
}
